import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Plus, X, Briefcase, Euro, Users } from "lucide-react";
import { toast } from "sonner";

/* ------- 9 unified stages (Pipeline + Collab) ------- */
const STAGES = [
  { v: "new_inquiry",   l: "New inquiry",   accent: "#706E6B" },
  { v: "qualified",     l: "Qualified",     accent: "#1B96FF" },
  { v: "terms_defined", l: "Terms defined", accent: "#0EA5E9" },
  { v: "negotiation",   l: "Negotiation",   accent: "#F59E0B" },
  { v: "confirmed",     l: "Confirmed",     accent: "#FE9339" },
  { v: "production",    l: "Production",    accent: "#FB923C" },
  { v: "scheduled",     l: "Scheduled",     accent: "#A855F7" },
  { v: "completed",     l: "Completed",     accent: "#2E844A" },
  { v: "closed_lost",   l: "Closed / Lost", accent: "#9CA3AF" },
];

const COLLAB_TO_STAGE = {
  submitted: "new_inquiry",
  "in-talks": "negotiation",
  confirmed: "confirmed",
  posted: "completed",
  declined: "closed_lost",
};
const STAGE_TO_COLLAB = {
  new_inquiry: "submitted",
  qualified: "submitted",
  terms_defined: "in-talks",
  negotiation: "in-talks",
  confirmed: "confirmed",
  production: "in-talks",
  scheduled: "in-talks",
  completed: "posted",
  closed_lost: "declined",
};

const OPEN = ["new_inquiry","qualified","terms_defined","negotiation","confirmed","production","scheduled"];
const STALE = { negotiation: 14, confirmed: 21, terms_defined: 21, production: 14, scheduled: 7 };
const MS = 86400000;

const isStale = (d) => OPEN.includes(d.stage) && (Date.now() - Date.parse(d.updated_at || d.created_at || 0)) / MS >= (STALE[d.stage] || 30);
const daysSince = (d) => Math.floor((Date.now() - Date.parse(d.updated_at || d.created_at || 0)) / MS) || 0;

const EMPTY_DEAL = {
  type: "brand", brand_name: "", stage: "new_inquiry", website: "", contact_person: "",
  contact_email: "", handle: "", platform: "", content_type: "",
  deal_value: 0, compensation_type: "Unknown", deliverables_count: 1, due_date: "", notes: "",
};
const EMPTY_COLLAB = {
  type: "collab", partner_name: "", handle: "", platform: "", status: "submitted",
  notes: "", contact: "", rate: "",
};

export default function Deals() {
  const [filter, setFilter] = useState("all"); // all | brand | collab
  const [pipeline, setPipeline] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_DEAL);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const load = async () => {
    try {
      await api.post("/api/pipeline/import-submissions");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not import brand proposals");
    }
    const [p, s, c] = await Promise.all([
      api.get("/api/pipeline/"),
      api.get("/api/pipeline/summary").catch(() => ({ data: null })),
      api.get("/api/collabs").catch(() => ({ data: [] })),
    ]);
    setPipeline(p.data); setCollabs(c.data); setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  /* Normalize both sources into one unified list */
  const items = useMemo(() => {
    const brands = pipeline.map(d => ({ ...d, _kind: "brand", _name: d.brand_name }));
    const quicks = collabs.map(c => ({
      ...c,
      _kind: "collab",
      _name: c.partner_name,
      stage: COLLAB_TO_STAGE[c.status] || "new_inquiry",
      deal_value: 0,
      platform: c.platform || "",
      handle: c.handle || "",
      product: c.notes || "",
      created_at: c.created_at,
      updated_at: c.updated_at,
    }));
    const merged = [...brands, ...quicks];
    if (filter === "brand")  return merged.filter(x => x._kind === "brand");
    if (filter === "collab") return merged.filter(x => x._kind === "collab");
    return merged;
  }, [pipeline, collabs, filter]);

  const cols = useMemo(() => {
    const by = {}; STAGES.forEach(s => by[s.v] = []);
    items.forEach(d => { (by[d.stage] || by.new_inquiry).push(d); });
    return by;
  }, [items]);

  const openNewBrand  = () => { setEditing("new"); setForm(EMPTY_DEAL); };
  const openNewCollab = () => { setEditing("new"); setForm(EMPTY_COLLAB); };
  const openEdit = (d) => {
    setEditing(d.id);
    if (d._kind === "brand") {
      setForm({ ...EMPTY_DEAL, ...d, type: "brand" });
    } else {
      setForm({ ...EMPTY_COLLAB, ...d, type: "collab" });
    }
  };
  const close = () => { setEditing(null); setForm(EMPTY_DEAL); };

  const save = async () => {
    try {
      if (form.type === "brand") {
        if (!form.brand_name?.trim()) return toast.error("Brand name required");
        const body = { ...form, deal_value: parseFloat(form.deal_value) || 0,
          deliverables_count: parseInt(form.deliverables_count) || 1 };
        delete body.type; delete body._kind; delete body._name;
        if (editing === "new") await api.post("/api/pipeline/", body);
        else await api.put(`/api/pipeline/${editing}`, body);
      } else {
        if (!form.partner_name?.trim()) return toast.error("Partner name required");
        const body = { partner_name: form.partner_name, handle: form.handle, platform: form.platform,
          status: form.status, notes: form.notes, contact: form.contact, rate: form.rate };
        if (editing === "new") await api.post("/api/collabs", body);
        else await api.put(`/api/collabs/${editing}`, body);
      }
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  /* drag & drop between columns */
  const onDragStart = (d) => setDraggedId(d.id + "::" + d._kind);
  const onDragOver = (e, stage) => { e.preventDefault(); setDragOver(stage); };
  const onDrop = async (e, stage) => {
    e.preventDefault(); setDragOver(null);
    if (!draggedId) return;
    const [id, kind] = draggedId.split("::");
    setDraggedId(null);
    if (kind === "brand") {
      await api.put(`/api/pipeline/${id}`, { stage });
    } else {
      const newStatus = STAGE_TO_COLLAB[stage] || "submitted";
      await api.put(`/api/collabs/${id}`, { status: newStatus });
    }
    load();
  };

  const totalDeals = items.length;
  const pipelineValue = pipeline.reduce((s, d) =>
    OPEN.includes(d.stage) ? s + (d.deal_value || 0) : s, 0);
  const closedValue = pipeline.reduce((s, d) =>
    d.stage === "completed" ? s + (d.deal_value || 0) : s, 0);

  return (
    <AppLayout
      title="Deals"
      overline="170 · BRAND PIPELINE + QUICK COLLABS · UNIFIED KANBAN"
      actions={
        <>
          <button onClick={openNewCollab} data-testid="deals-new-collab" className="btn-ghost inline-flex items-center gap-2">
            <Users size={15} /> Quick collab
          </button>
          <button onClick={openNewBrand} data-testid="deals-new-brand" className="btn-primary inline-flex items-center gap-2">
            <Plus size={15} /> New brand deal
          </button>
        </>
      }
    >
      {/* KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6 fade-up" data-testid="deals-summary">
        <Stat label="Total cards" value={totalDeals} testid="deals-total" />
        <Stat label="Pipeline value" value={`€${pipelineValue.toFixed(0)}`} accent testid="deals-value" />
        <Stat label="Closed (completed)" value={`€${closedValue.toFixed(0)}`} testid="deals-closed" />
        <Stat label="Confirmed deals" value={summary?.by_stage?.confirmed ?? 0} testid="deals-confirmed" />
      </section>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-5" data-testid="deals-filter">
        <FilterChip label="All"        count={pipeline.length + collabs.length} active={filter === "all"}    onClick={() => setFilter("all")}    testid="deals-f-all" />
        <FilterChip label="Brand deals" count={pipeline.length} active={filter === "brand"}  onClick={() => setFilter("brand")}  testid="deals-f-brand" />
        <FilterChip label="Quick collabs" count={collabs.length} active={filter === "collab"} onClick={() => setFilter("collab")} testid="deals-f-collab" />
        <span className="text-[12px] ml-auto" style={{ color: "var(--gio-muted-2)" }}>Drag cards between stages →</span>
      </div>

      {/* Kanban */}
      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 pb-2">
        <div className="grid grid-cols-9 gap-3 min-w-[1500px] xl:min-w-0" data-testid="deals-board">
        {STAGES.map((s) => (
          <div key={s.v}
            className={`bg-white border p-3 min-h-[260px] rounded-lg ${dragOver === s.v ? "drop-active" : ""}`}
            style={{ borderColor: "var(--gio-border)" }}
            onDragOver={(e) => onDragOver(e, s.v)}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => onDrop(e, s.v)}
            data-testid={`deals-col-${s.v}`}
          >
            <div className="flex items-center justify-between pb-2 mb-3 border-b" style={{ borderColor: "var(--gio-border)" }}>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: s.accent }} />
                <span className="font-heading text-[13px]">{s.l}</span>
              </div>
              <span className="text-[12px] font-mono-ui" style={{ color: "var(--gio-muted-2)" }}>{(cols[s.v] || []).length}</span>
            </div>
            <div className="space-y-2">
              {(cols[s.v] || []).map((d) => {
                const stale = isStale(d);
                return (
                  <div key={d._kind + d.id}
                    draggable
                    onDragStart={() => onDragStart(d)}
                    onClick={() => openEdit(d)}
                    data-testid={`deals-card-${d._kind}-${d.id}`}
                    className="bg-white border rounded-md p-3 cursor-pointer transition hover:shadow-md"
                    style={{
                      borderColor: stale ? "var(--gio-primary)" : "var(--gio-border)",
                      borderLeftWidth: 3,
                      borderLeftColor: d._kind === "collab" ? "var(--gio-secondary)" : s.accent,
                    }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-heading text-[14px] truncate flex-1">{d._name}</div>
                      {stale && <span className="text-[10px] font-bold uppercase" style={{ color: "var(--gio-primary)" }} title={`${daysSince(d)}d stale`}>STALE</span>}
                    </div>
                    {d._kind === "collab" && (
                      <div className="text-[10px] font-bold uppercase mb-1" style={{ color: "var(--gio-secondary)" }}>QUICK COLLAB</div>
                    )}
                    {d.submission_id && (
                      <div className="text-[10px] font-bold uppercase mb-1" style={{ color: "var(--gio-primary)" }}>FROM /APPLY</div>
                    )}
                    {(d.product || d.notes) && (
                      <div className="text-[12px] line-clamp-2 mb-2" style={{ color: "var(--gio-muted)" }}>{d.product || d.notes}</div>
                    )}
                    <div className="flex items-center justify-between text-[11px]">
                      <span style={{ color: "var(--gio-muted-2)" }}>{d.platform || "—"} · {daysSince(d)}d</span>
                      {d._kind === "brand" && d.deal_value > 0 && (
                        <span className="font-mono-ui flex items-center gap-1" style={{ color: "var(--gio-secondary)" }}>
                          <Euro size={11} />{d.deal_value.toFixed(0)}
                        </span>
                      )}
                      {d._kind === "collab" && d.rate && (
                        <span className="font-mono-ui" style={{ color: "var(--gio-secondary)" }}>{d.rate}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {(cols[s.v] || []).length === 0 && (
                <div className="text-center text-[12px] py-3" style={{ color: "var(--gio-muted-3)" }}>—</div>
              )}
            </div>
          </div>
        ))}
        </div>
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close} data-testid="deals-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "var(--gio-shadow-lg)" }}>
            <div className="p-6 flex items-center justify-between" style={{ borderBottom: "1px solid var(--gio-border)" }}>
              <div>
                <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: "var(--gio-primary)" }}>
                  {editing === "new" ? "NEW" : "EDIT"} · {form.type === "brand" ? "BRAND DEAL" : "QUICK COLLAB"}
                </div>
                <h2 className="font-heading text-2xl mt-1">{form.brand_name || form.partner_name || "Untitled"}</h2>
              </div>
              <button onClick={close} data-testid="deals-modal-close"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Toggle type only in NEW mode */}
              {editing === "new" && (
                <div className="flex gap-2 mb-2">
                  <button onClick={() => setForm({ ...EMPTY_DEAL })}   className={`px-4 py-2 rounded text-[13px] font-semibold border ${form.type === "brand" ? "bg-[var(--gio-primary-soft)] border-[var(--gio-primary)] text-[color:var(--gio-primary)]" : "border-[var(--gio-border)]"}`}>Brand deal</button>
                  <button onClick={() => setForm({ ...EMPTY_COLLAB })} className={`px-4 py-2 rounded text-[13px] font-semibold border ${form.type === "collab" ? "bg-orange-50 border-[var(--gio-secondary)] text-[color:var(--gio-secondary)]" : "border-[var(--gio-border)]"}`}>Quick collab</button>
                </div>
              )}
              {form.type === "brand" ? (
                <>
                  <Field l="Brand Name *"><input data-testid="deals-f-brand" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="gf-input" /></Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field l="Stage">
                      <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="deals-f-stage" className="gf-input">
                        {STAGES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                      </select>
                    </Field>
                    <Field l="Website"><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="gf-input" /></Field>
                    <Field l="Contact Person"><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="gf-input" /></Field>
                    <Field l="Contact Email"><input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="gf-input" /></Field>
                    <Field l="Handle"><input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="gf-input" /></Field>
                    <Field l="Platform"><input value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="gf-input" /></Field>
                    <Field l="Compensation Type">
                      <select value={form.compensation_type} onChange={(e) => setForm({ ...form, compensation_type: e.target.value })} className="gf-input">
                        {["Unknown","Paid","Product","Affiliate","Hybrid"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field l="Deal Value (€)"><input type="number" step="0.01" value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: e.target.value })} className="gf-input" /></Field>
                    <Field l="# Deliverables"><input type="number" value={form.deliverables_count} onChange={(e) => setForm({ ...form, deliverables_count: e.target.value })} className="gf-input" /></Field>
                    <Field l="Due Date"><input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="gf-input" /></Field>
                  </div>
                  <Field l="Product / Offer Description"><textarea value={form.product || ""} onChange={(e) => setForm({ ...form, product: e.target.value })} rows={3} className="gf-input" /></Field>
                  <Field l="Internal Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="gf-input" /></Field>
                </>
              ) : (
                <>
                  <Field l="Partner name *"><input data-testid="deals-f-partner" value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className="gf-input" /></Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field l="Handle"><input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="@username" className="gf-input" /></Field>
                    <Field l="Platform"><input value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="gf-input" /></Field>
                    <Field l="Status">
                      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="gf-input">
                        {["submitted","in-talks","confirmed","posted","declined"].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field l="Rate / terms"><input value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="$500 + revshare..." className="gf-input" /></Field>
                    <Field l="Contact"><input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="gf-input" /></Field>
                  </div>
                  <Field l="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="gf-input" /></Field>
                </>
              )}
            </div>
            <div className="p-6 flex justify-between gap-3" style={{ borderTop: "1px solid var(--gio-border)" }}>
              <div className="flex gap-3 ml-auto">
                <button onClick={close} className="btn-ghost">Cancel</button>
                <button onClick={save} data-testid="deals-save" className="btn-primary inline-flex items-center gap-2">
                  <Briefcase size={15} /> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function Field({ l, children }) {
  return (
    <div>
      <div className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--gio-muted)" }}>{l}</div>
      {children}
    </div>
  );
}

function FilterChip({ label, count, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-[13px] font-semibold rounded border transition ${active ? "bg-[var(--gio-primary-soft)] border-[var(--gio-primary)] text-[color:var(--gio-primary)]" : "bg-white border-[var(--gio-border)] hover:border-[var(--gio-primary)]"}`}>
      {label} <span className="ml-1 font-mono-ui text-[11px]" style={{ color: "var(--gio-muted-2)" }}>{count}</span>
    </button>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div className="bg-white border p-5 rounded-lg" data-testid={testid}
      style={{ borderColor: accent ? "var(--gio-primary)" : "var(--gio-border)", boxShadow: "var(--gio-shadow-sm)" }}>
      <div className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--gio-muted)" }}>{label}</div>
      <div className="font-heading text-3xl" style={{ color: accent ? "var(--gio-primary)" : "var(--gio-text)" }}>{value}</div>
    </div>
  );
}
